/** Dashboard-only recipe management API. All endpoints are server-gated. */
import { open_api_with_cookies } from "./network";

const ADMIN_BASE = "/recipe/admin";

export const unwrap = (response) => {
  const value = response?.data;
  if (value && Object.prototype.hasOwnProperty.call(value, "data")) {
    return value.data;
  }
  return value;
};

export const unwrapList = (response) => {
  const raw = unwrap(response);
  if (!raw) return { items: [], count: 0, total: 0 };
  if (Array.isArray(raw)) {
    return { items: raw, count: raw.length, total: raw.length };
  }
  if (Array.isArray(raw.results)) {
    return {
      items: raw.results,
      count: raw.results.length,
      total: raw.count ?? raw.results.length,
      next: raw.next,
      previous: raw.previous,
    };
  }
  if (Array.isArray(raw.data)) {
    return {
      items: raw.data,
      count: raw.count ?? raw.data.length,
      total: raw.total_count ?? raw.data.length,
      page: raw.page,
      maxPages: raw.max_pages,
      nextPage: raw.next_page,
      previousPage: raw.previous_page,
    };
  }
  return { items: [], count: 0, total: 0 };
};

export const getApiError = (error, fallback = "Something went wrong.") => {
  const raw = error?.response?.data;
  const detail = raw?.detail || raw?.error || raw?.message;
  if (typeof detail === "string") return detail;
  const payload = raw?.data && typeof raw.data === "object" ? raw.data : raw;
  if (payload && typeof payload === "object") {
    const first = Object.entries(payload)[0];
    if (first) {
      const message = Array.isArray(first[1]) ? first[1][0] : first[1];
      return `${first[0].replaceAll("_", " ")}: ${message}`;
    }
  }
  return fallback;
};

export const listRecipes = async (params = {}) =>
  unwrapList(
    await open_api_with_cookies.get(`${ADMIN_BASE}/recipes/`, { params })
  );

export const getRecipeSummary = async () =>
  unwrap(await open_api_with_cookies.get(`${ADMIN_BASE}/recipes/summary/`));

export const getRecipe = async (id) =>
  unwrap(await open_api_with_cookies.get(`${ADMIN_BASE}/recipes/${id}/`));

export const createRecipe = async (payload) =>
  unwrap(await open_api_with_cookies.post(`${ADMIN_BASE}/recipes/`, payload));

export const updateRecipe = async (id, payload) =>
  unwrap(
    await open_api_with_cookies.patch(`${ADMIN_BASE}/recipes/${id}/`, payload)
  );

export const deleteRecipe = async (id) => {
  await open_api_with_cookies.delete(`${ADMIN_BASE}/recipes/${id}/`);
  return true;
};

export const toggleRecipeActive = async (id) =>
  unwrap(
    await open_api_with_cookies.post(
      `${ADMIN_BASE}/recipes/${id}/toggle-active/`
    )
  );

export const uploadRecipeBanner = async (id, file) => {
  return uploadRecipeMedia(id, "banner", file);
};

export const uploadRecipeMedia = async (id, field, file) => {
  const data = new FormData();
  data.append("file", file);
  data.append("field", field);
  return unwrap(
    await open_api_with_cookies.post(
      `${ADMIN_BASE}/recipes/${id}/upload-media/`,
      data,
      {
        headers: { "Content-Type": "multipart/form-data" },
        transformRequest: [(body) => body],
      }
    )
  );
};

const lookup = async (path, params = {}) => {
  const response = await open_api_with_cookies.get(`${ADMIN_BASE}/${path}/`, {
    params,
  });
  const raw = unwrap(response);
  if (Array.isArray(raw)) return raw;
  return unwrapList(response).items;
};

export const listRecipeCategories = (params = {}) =>
  lookup("recipe-categories", params);
export const listIngredientMaster = (params = {}) =>
  lookup("ingredient-master", { page_size: 200, ...params });
export const listRecipeTypes = () => lookup("recipe-types");
export const listRecipeAudiences = () => lookup("recipe-audiences");
export const listRecipeExperts = (params = {}) =>
  lookup("recipe-experts", params);
export const listRecipeTags = (params = {}) => lookup("tags", params);

export const RESOURCE_ENDPOINTS = {
  categories: "recipe-categories",
  ingredients: "ingredient-master",
  recipeIngredients: "recipe-ingredients",
  directions: "directions",
  nutrition: "nutrition",
  chips: "filter-chips",
  views: "views",
  favourites: "favourites",
  likes: "likes",
  tags: "tags",
  collections: "collections",
  collectionItems: "collection-items",
  homeSections: "home-sections",
  homeSectionItems: "home-section-items",
  interactions: "interactions",
  cookSessions: "cook-sessions",
  mealPlans: "meal-plans",
  mealPlanItems: "meal-plan-items",
  experienceConfigurations: "experience-configurations",
  mealPlanAIConfigurations: "meal-plan-ai-configurations",
};

export const listRecipeResource = async (resource, params = {}) => {
  const endpoint = RESOURCE_ENDPOINTS[resource];
  return unwrapList(
    await open_api_with_cookies.get(`${ADMIN_BASE}/${endpoint}/`, {
      params: { page_size: 200, ...params },
    })
  );
};

const resourceUrl = (resource, id = null) => {
  const endpoint = RESOURCE_ENDPOINTS[resource];
  return `${ADMIN_BASE}/${endpoint}/${id ? `${id}/` : ""}`;
};

export const saveRecipeResource = async (
  resource,
  payload,
  id = null,
  file = null,
  fileField = null
) => {
  let body = payload;
  let config;
  if (file && fileField) {
    body = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((item) => body.append(key, item));
      } else if (typeof value === "object") {
        body.append(key, JSON.stringify(value));
      } else {
        body.append(key, value);
      }
    });
    body.append(fileField, file);
    config = {
      headers: { "Content-Type": "multipart/form-data" },
      transformRequest: [(value) => value],
    };
  }
  const response = id
    ? await open_api_with_cookies.patch(resourceUrl(resource, id), body, config)
    : await open_api_with_cookies.post(resourceUrl(resource), body, config);
  return unwrap(response);
};

export const deleteRecipeResource = async (resource, id) => {
  await open_api_with_cookies.delete(resourceUrl(resource, id));
  return true;
};
